<template>
    <div :style="{backgroundColor:layout?.pageBackground?.color}">
        <section class="contact">
            <div class="contact__container">
                <div class="contact__info">
                    <!-- Dynamic Company Logo -->
                    <img 
                        v-if="layout?.box1?.companyLogo?.preview" 
                        :src="layout.box1.companyLogo.preview" 
                        alt="Company Logo" 
                        class="contact__logo"
                    >
                    <img 
                        v-else 
                        src="ImageBackgroundSenseofSelf.png" 
                        alt="VestiGO Logo" 
                        class="contact__logo"
                    >

                    <div class="contact__details">
                        <!-- Dynamic Business Address -->
                        <div class="contact__detail">
                            <h4 class="contact__label">Address</h4>
                            <p 
                                class="contact__text" 
                                :style="getTextStyle(layout?.box1?.businessAddress)"
                            >
                            {{ layout?.box1?.businessAddress?.text }}
                                <!-- {{ formatAddress(layout?.box1?.businessAddress?.text) }} -->
                            </p>
                        </div>

                        <!-- Dynamic Business Phone -->
                        <div class="contact__detail">
                            <h4 class="contact__label">Telephone Number</h4>
                            <p 
                                class="contact__text"
                                :style="getTextStyle(layout?.box1?.businessPhone)"
                            >
                                {{ layout?.box1?.businessPhone?.text || '+3 045 224 33 12' }}
                            </p>
                        </div>

                        <!-- Dynamic Business Email -->
                        <div class="contact__detail">
                            <h4 class="contact__label">Email Address</h4>
                            <p 
                                class="contact__text"
                                :style="getTextStyle(layout?.box1?.businessEmail)"
                            >
                                <a 
                                    :href="'mailto:' + (layout?.box1?.businessEmail?.text || 'info@vestigobrandsolutions.com')"
                                    :style="getEmailStyle(layout?.box1?.businessEmail)"
                                >
                                    {{ layout?.box1?.businessEmail?.text || 'info@vestigobrandsolutions.com' }}
                                </a>
                            </p>
                        </div>

                        <!-- Dynamic Social Media Icons -->
                        <div v-if="layout?.box1?.socialMediaIcons?.length > 0" class="contact__detail">
                            <h4 class="contact__label">Follow Us</h4>
                            <div class="contact__social">
                                <a 
                                    v-for="(icon, index) in layout.box1.socialMediaIcons" 
                                    :key="index"
                                    :href="icon.url || '#'"
                                    :target="icon.target || '_blank'"
                                    class="contact__social-link"
                                    v-if="icon.preview"
                                    :title="icon.name"
                                >
                                    <img :src="icon.preview" :alt="icon.name" class="contact__social-icon">
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Dynamic Google Maps -->
                <div class="contact__map">
                    <h4 class="contact__map-title">
                        {{ getMapTitle() }}
                    </h4>
                    
                    <!-- Show map if enabled -->
                    <div v-if="layout?.box1?.googleMaps?.enabled && layout?.box1?.googleMaps?.address">
                        <!-- Google Maps Embed (No API Key) -->
                        <iframe
                            v-if="layout.box1.googleMaps.provider === 'embed'"
                            :src="getGoogleEmbedUrl()"
                            width="100%" 
                            :height="layout.box1.googleMaps.height || 250"
                            style="border:0;" 
                            allowfullscreen="" 
                            loading="lazy"
                            referrerpolicy="no-referrer-when-downgrade" 
                            class="contact__iframe">
                        </iframe>

                        <!-- OpenStreetMap with Leaflet -->
                        <div 
                            v-else-if="layout.box1.googleMaps.provider === 'openstreet'"
                            :id="'osm-map-' + mapId"
                            class="contact__iframe osm-map"
                            :style="{ height: (layout.box1.googleMaps.height || 250) + 'px' }"
                        ></div>

                        <!-- Bing Maps -->
                        <div 
                            v-else-if="layout.box1.googleMaps.provider === 'bing'"
                            :id="'bing-map-' + mapId"
                            class="contact__iframe bing-map"
                            :style="{ height: (layout.box1.googleMaps.height || 250) + 'px' }"
                        ></div>

                        <!-- Mapbox -->
                        <div 
                            v-else-if="layout.box1.googleMaps.provider === 'mapbox'"
                            :id="'mapbox-map-' + mapId"
                            class="contact__iframe mapbox-map"
                            :style="{ height: (layout.box1.googleMaps.height || 250) + 'px' }"
                        ></div>

                        <!-- Google Maps (with API) -->
                        <div 
                            v-else
                            :id="'google-map-' + mapId"
                            class="contact__iframe google-map"
                            :style="{ height: (layout.box1.googleMaps.height || 250) + 'px' }"
                        ></div>
                    </div>

                    <!-- Fallback: Default iframe or disabled message -->
                    <div v-else>
                        <iframe
                            v-if="!layout?.box1?.googleMaps?.enabled"
                            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d28026.36502634662!2d77.3076154!3d28.5909068!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce9db3d51865f%3A0x49bc7de786746dad!2sInfinity%20Soft%20Systems!5e0!3m2!1sen!2sin!4v1752054327581!5m2!1sen!2sin"
                            width="100%" 
                            height="250" 
                            style="border:0;" 
                            allowfullscreen="" 
                            loading="lazy"
                            referrerpolicy="no-referrer-when-downgrade" 
                            class="contact__iframe">
                        </iframe>
                        <div v-else class="contact__map-placeholder">
                            <p>Map configuration incomplete. Please provide an address.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export default {
    name: "PageLayoutEight",
    props: {
        layout: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            mapId: Math.random().toString(36).substr(2, 9) // Unique ID for maps
        }
    },

    mounted() {
        // Initialize maps after component is mounted
        this.initializeMaps();
    },

    watch: {
        layout: {
            handler() {
                // Re-initialize maps when layout changes
                this.$nextTick(() => {
                    this.initializeMaps();
                });
            },
            deep: true
        }
    },

    methods: {
        getTextStyle(textObj) {
            if (!textObj) return {};
            
            return {
                fontSize: (textObj.fontSize || 22) + 'px',
                color: textObj.color || '#333',
                backgroundColor: textObj.backgroundColor || 'transparent',
                fontFamily: textObj.fontFamily || 'Arial',
                fontWeight: textObj.bold ? 'bold' : 'normal'
            };
        },

        getEmailStyle(textObj) {
            if (!textObj) return { color: '#333', textDecoration: 'none' };
            
            return {
                fontSize: (textObj.fontSize || 22) + 'px',
                color: textObj.color || '#333',
                fontFamily: textObj.fontFamily || 'Arial',
                fontWeight: textObj.bold ? 'bold' : 'normal',
                textDecoration: 'none'
            };
        },

        formatAddress(address) {
            if (!address) return null;
            // Replace line breaks with <br> tags for HTML display
            return address.replace(/\n/g, '<br>');
        },

        getMapTitle() {
            const provider = this.layout?.box1?.googleMaps?.provider;
            const providerNames = {
                'google': 'GOOGLE MAP BUSINESS LOCATION',
                'openstreet': 'OPENSTREETMAP BUSINESS LOCATION',
                'bing': 'BING MAP BUSINESS LOCATION',
                'mapbox': 'MAPBOX BUSINESS LOCATION',
                'embed': 'GOOGLE MAP BUSINESS LOCATION'
            };
            return providerNames[provider] || 'GOOGLE MAP BUSINESS LOCATION';
        },

        getGoogleEmbedUrl() {
            const address = this.layout?.box1?.googleMaps?.address;
            const zoom = this.layout?.box1?.googleMaps?.zoom || 14;
            
            if (!address) return '';
            
            // Create Google Maps Embed URL
            const encodedAddress = encodeURIComponent(address);
            return `https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${encodedAddress}&zoom=${zoom}`;
        },

        async initializeMaps() {
            const mapConfig = this.layout?.box1?.googleMaps;
            
            if (!mapConfig?.enabled || !mapConfig?.address) return;

            try {
                switch (mapConfig.provider) {
                    case 'openstreet':
                        await this.initOpenStreetMap();
                        break;
                    case 'bing':
                        await this.initBingMap();
                        break;
                    case 'mapbox':
                        await this.initMapbox();
                        break;
                    case 'google':
                        await this.initGoogleMap();
                        break;
                    // 'embed' case is handled in template with iframe
                }
            } catch (error) {
                console.warn('Map initialization failed:', error);
            }
        },

        async initOpenStreetMap() {
            // Load Leaflet.js if not already loaded
            if (!window.L) {
                await this.loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
                await this.loadCSS('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
            }

            const mapElement = document.getElementById(`osm-map-${this.mapId}`);
            if (!mapElement) return;

            const address = this.layout.box1.googleMaps.address;
            const zoom = parseInt(this.layout.box1.googleMaps.zoom) || 14;

            // Geocode the address using Nominatim (free)
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
                const data = await response.json();
                
                if (data.length > 0) {
                    const lat = parseFloat(data[0].lat);
                    const lon = parseFloat(data[0].lon);

                    // Create the map
                    const map = window.L.map(mapElement).setView([lat, lon], zoom);
                    
                    // Add OpenStreetMap tiles
                    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap contributors'
                    }).addTo(map);

                    // Add marker
                    window.L.marker([lat, lon]).addTo(map)
                        .bindPopup(address)
                        .openPopup();
                }
            } catch (error) {
                console.error('OpenStreetMap initialization failed:', error);
                mapElement.innerHTML = '<div class="map-error">Failed to load map</div>';
            }
        },

        async initBingMap() {
            // Simple placeholder for Bing Maps
            const mapElement = document.getElementById(`bing-map-${this.mapId}`);
            if (!mapElement) return;

            mapElement.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f0f0f0; border-radius: 8px;">
                    <div style="text-align: center;">
                        <i class="fab fa-microsoft" style="font-size: 40px; color: #0078d4; margin-bottom: 10px;"></i>
                        <p><strong>Bing Maps</strong></p>
                        <p>${this.layout.box1.googleMaps.address}</p>
                        <small>Bing Maps integration available</small>
                    </div>
                </div>
            `;
        },

        async initMapbox() {
            // Simple placeholder for Mapbox
            const mapElement = document.getElementById(`mapbox-map-${this.mapId}`);
            if (!mapElement) return;

            mapElement.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: linear-gradient(135deg, #4264fb 0%, #04befe 100%); border-radius: 8px; color: white;">
                    <div style="text-align: center;">
                        <i class="fas fa-map-marked-alt" style="font-size: 40px; margin-bottom: 10px;"></i>
                        <p><strong>Mapbox</strong></p>
                        <p>${this.layout.box1.googleMaps.address}</p>
                        <small>Mapbox integration available</small>
                    </div>
                </div>
            `;
        },

        async initGoogleMap() {
            // Simple placeholder for Google Maps
            const mapElement = document.getElementById(`google-map-${this.mapId}`);
            if (!mapElement) return;

            mapElement.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 8px; color: white;">
                    <div style="text-align: center;">
                        <i class="fab fa-google" style="font-size: 40px; margin-bottom: 10px;"></i>
                        <p><strong>Google Maps</strong></p>
                        <p>${this.layout.box1.googleMaps.address}</p>
                        <small>Requires API key</small>
                    </div>
                </div>
            `;
        },

        loadScript(src) {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve();
                    return;
                }
                
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        },

        loadCSS(href) {
            return new Promise((resolve) => {
                if (document.querySelector(`link[href="${href}"]`)) {
                    resolve();
                    return;
                }
                
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = href;
                link.onload = resolve;
                document.head.appendChild(link);
            });
        }
    }
}
</script>

<style scoped>
.contact {
    padding: 40px 20px;
    /* background-color: #fff; */
    font-family: Arial, sans-serif;
}

.contact__container {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 40px;
    max-width: 1200px;
    margin: 0 auto;
    align-items: flex-start;
}

.contact__info {
    flex: 1 1 300px;
}

.contact__logo {
    max-width: 180px;
    margin-bottom: 20px;
    border-radius: 5px;
}

.contact__details {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.contact__detail {
    line-height: 1.6;
}

.contact__label {
    font-weight: bold;
    margin-bottom: 5px;
    font-size: 22px;
}

.contact__text {
    margin: 0;
    color: #333;
    font-size: 22px;
}

.contact__text a {
    color: inherit;
    text-decoration: none;
}

.contact__text a:hover {
    text-decoration: underline;
}

/* Social Media Icons */
.contact__social {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    margin-top: 10px;
}

.contact__social-link {
    display: inline-block;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #ddd;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.contact__social-link:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.contact__social-icon {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.contact__map {
    flex: 1 1 400px;
}

.contact__map-title {
    font-size: 22px;
    font-weight: bold;
    margin-bottom: 15px;
    text-align: center;
}

.contact__iframe {
    width: 100%;
    height: 250px;
    border: 0;
    border-radius: 8px;
}

.contact__map-placeholder {
    height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    color: #6c757d;
    text-align: center;
}

/* Map specific styles */
.osm-map, .bing-map, .mapbox-map, .google-map {
    border-radius: 8px;
    overflow: hidden;
}

.map-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: #f8d7da;
    color: #721c24;
    border-radius: 8px;
    text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
    .contact__container {
        flex-direction: column;
        align-items: center;
    }

    .contact__map-title {
        text-align: center;
    }

    .contact__iframe {
        height: 200px;
    }

    .contact__logo {
        margin: 0 auto 20px;
        display: block;
    }

    .contact__info,
    .contact__map {
        width: 100%;
    }

    .contact__social {
        justify-content: center;
    }
    .contact__social-link {
        width: 40px;
        height: 40px;
    }
    .contact__social-icon {
        width: 100%;
        height: 100%;
    }
    .contact__label {
        font-size: 18px;
    }
    .contact__text {
        font-size: 18px;
    }
    .contact__map-placeholder {
        height: 200px;
    }
    .contact__map {
        width: 100%;
        flex: 1;
    }
    .contact__container {
        padding: 20px;
    }
    .contact__info {
        margin-bottom: 20px;
    }
    .contact__details {
        gap: 15px;
    }
    .contact__detail {
        line-height: 1.4;
    }
    .contact__label {
        font-size: 20px;
    }
    .contact__text {
        font-size: 20px;
    }
    .contact__map-title {
        font-size: 20px;
    }
    .contact__iframe {
        height: 200px;
    }
    .contact__map-placeholder {
        height: 200px;
    }
    .contact__logo {
        max-width: 150px;
    }
    .contact__social-link {
        width: 35px;
        height: 35px;
    }
    .contact__social-icon {
        width: 100%;
        height: 100%;
    }
    .contact__text a {
        font-size: 18px;
    }
    .contact__text {
        font-size: 18px;
    }
    .contact__label {
        font-size: 18px;
    }
}
</style>